import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Divider,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Description as PaperIcon,
  Bolt as StrategiesIcon,
  School as AcademyIcon,
  AccountBalanceWallet as PortfolioIcon,
  Rocket as LiveTradingIcon,
  Settings as SettingsIcon,
  Lock as LockIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSecurityTier } from '../../contexts/SecurityTierContext';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  requiredTier: 1 | 2 | 3;
  lockHint?: string;
}

const DRAWER_WIDTH = 200;
export { DRAWER_WIDTH };

const NAV_ITEMS: NavItem[] = [
  { id: 'dashboard', label: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', requiredTier: 1 },
  { id: 'paper-trading', label: 'Paper Trading', icon: <PaperIcon />, path: '/paper-trading', requiredTier: 1 },
  { id: 'strategies', label: 'Strategien', icon: <StrategiesIcon />, path: '/strategies', requiredTier: 1 },
  { id: 'academy', label: 'Academy', icon: <AcademyIcon />, path: '/academy', requiredTier: 1 },
  { id: 'backtesting', label: 'Backtesting', icon: <QueryStatsIcon />, path: '/backtesting', requiredTier: 1 },
  { id: 'portfolio', label: 'Portfolio', icon: <PortfolioIcon />, path: '/portfolio', requiredTier: 2, lockHint: '2FA nötig' },
  { id: 'live-trading', label: 'Live Trading', icon: <LiveTradingIcon />, path: '/live-trading', requiredTier: 3, lockHint: 'API nötig' },
];

const Sidebar: React.FC<SidebarProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const { canAccess } = useSecurityTier();
  const { logout } = useAuth();

  const handleNavClick = (item: NavItem) => {
    if (canAccess(item.requiredTier)) {
      navigate(item.path);
      if (isMobile) onClose();
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', pt: 8 }}>
      <Box sx={{ flexGrow: 1, overflowY: 'auto', py: 1 }}>
        <List>
          {NAV_ITEMS.map((item) => {
            const isActive = location.pathname === item.path;
            const isLocked = !canAccess(item.requiredTier);

            return (
              <ListItem key={item.id} disablePadding>
                <ListItemButton
                  onClick={() => handleNavClick(item)}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 2,
                    opacity: isLocked ? 0.5 : 1,
                    backgroundColor: isActive
                      ? alpha(theme.palette.primary.main, 0.1)
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: isLocked
                        ? 'transparent'
                        : alpha(theme.palette.primary.main, 0.05),
                    },
                    cursor: isLocked ? 'not-allowed' : 'pointer',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                      minWidth: 36,
                    }}
                  >
                    {isLocked ? <LockIcon /> : item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontSize: '0.85rem',
                        fontWeight: isActive ? 600 : 500,
                        color: isActive ? theme.palette.primary.main : theme.palette.text.primary,
                      },
                    }}
                  />
                  {isLocked && item.lockHint && (
                    <Typography
                      variant="caption"
                      sx={{ color: 'warning.main', fontSize: '0.65rem', whiteSpace: 'nowrap' }}
                    >
                      {item.lockHint}
                    </Typography>
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>

        <Divider sx={{ mx: 2, my: 1 }} />

        <List>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                navigate('/settings');
                if (isMobile) onClose();
              }}
              sx={{
                mx: 1,
                borderRadius: 2,
                backgroundColor:
                  location.pathname === '/settings'
                    ? alpha(theme.palette.primary.main, 0.1)
                    : 'transparent',
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText
                primary="Einstellungen"
                sx={{
                  '& .MuiListItemText-primary': {
                    fontSize: '0.875rem',
                    fontWeight: location.pathname === '/settings' ? 600 : 500,
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>

      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: 2,
            color: theme.palette.error.main,
            '&:hover': {
              backgroundColor: alpha(theme.palette.error.main, 0.05),
            },
          }}
        >
          <ListItemIcon sx={{ color: 'inherit', minWidth: 32 }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Abmelden" />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'persistent'}
      open={open}
      onClose={onClose}
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;
